import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Stack,
  Chip,
  Button,
  Tooltip,
  Avatar,
  Link,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PeopleIcon from '@mui/icons-material/People';
import NumbersIcon from '@mui/icons-material/Numbers';
import LinkIcon from '@mui/icons-material/Link';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { getFiscalYearEndLabel } from './fiscalYearEnd';

export function getCompanyInitials(name: string): string {
  return name
    .split(' ')
    .filter((word) => /^[A-Z]/.test(word))
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2) || name.slice(0, 2).toUpperCase();
}

export function getStatusColor(status: string): 'success' | 'error' | 'warning' | 'default' {
  const statusUpper = String(status || '').toUpperCase();
  if (statusUpper === 'ACTIVE') return 'success';
  if (statusUpper === 'WOUND_UP' || statusUpper === 'DISSOLVED') return 'error';
  if (statusUpper === 'INACTIVE') return 'warning';
  return 'default';
}

interface CompanyHeaderProps {
  company: Record<string, unknown> | null;
  directorsCount: number;
  shareholdersCount: number;
  onEdit: () => void;
  onEnhance: () => void;
  onValidate: () => void;
  onAddDirector: () => void;
  onAddShareholder: () => void;
  onDownloadPdf: () => void;
  hasPermission: (permission: string) => boolean;
}

const CompanyHeader: React.FC<CompanyHeaderProps> = ({
  company,
  directorsCount,
  shareholdersCount,
  onEdit,
  onEnhance,
  onValidate,
  onAddDirector,
  onAddShareholder,
  onDownloadPdf,
  hasPermission,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!company) return null;
  const name = String(company.name ?? 'Company');

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2 }}>
      <Box
        display="flex"
        alignItems={{ xs: 'stretch', md: 'flex-start' }}
        justifyContent="space-between"
        gap={2}
        flexDirection={{ xs: 'column', md: 'row' }}
      >
        <Box display="flex" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={2} minWidth={0}>
          <Avatar
            sx={{
              width: { xs: 56, sm: 80 },
              height: { xs: 56, sm: 80 },
              bgcolor: 'secondary.main',
              fontSize: { xs: '1.25rem', sm: '2rem' },
              flexShrink: 0,
            }}
          >
            {getCompanyInitials(name)}
          </Avatar>
          <Box minWidth={0}>
            <Typography variant="h4" gutterBottom sx={{ mb: 0.5, overflowWrap: 'anywhere' }}>
              {name}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap alignItems="center">
              <Chip
                icon={<BusinessIcon />}
                label={String(company.jurisdiction || 'Unknown')}
                size="small"
                variant="outlined"
              />
              {company.status ? (
                <Chip
                  label={String(company.status)}
                  size="small"
                  color={getStatusColor(String(company.status))}
                />
              ) : null}
              <Chip
                icon={<CalendarTodayIcon />}
                label={`Fiscal Year End: ${getFiscalYearEndLabel(company.fiscalYearEndMonth)}`}
                size="small"
                variant="outlined"
              />
              {company.registrationNumber ? (
                <Chip
                  icon={<NumbersIcon />}
                  label={String(company.registrationNumber)}
                  size="small"
                  variant="outlined"
                />
              ) : null}
              {company.corporateFilingUrl ? (
                <Link
                  href={String(company.corporateFilingUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: { xs: 0, sm: 1 }, minHeight: 32 }}
                >
                  <LinkIcon fontSize="small" />
                  View registry
                </Link>
              ) : null}
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              <Typography variant="body2" color="text.secondary">
                <PeopleIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                {directorsCount} director{directorsCount !== 1 ? 's' : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <AccountBalanceIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                {shareholdersCount} shareholder{shareholdersCount !== 1 ? 's' : ''}
              </Typography>
            </Stack>
          </Box>
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          flexWrap="wrap"
          sx={{ gap: 1, alignItems: { xs: 'stretch', sm: 'center' } }}
        >
          {hasPermission('companies:update') && (
            <Tooltip title="Edit company details">
              <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={onEdit} fullWidth={isMobile}>
                Edit
              </Button>
            </Tooltip>
          )}
          {hasPermission('enrichment:execute') && (
            <Tooltip title="Enhance from public records">
              <Button size="small" variant="contained" startIcon={<AutoAwesomeIcon />} onClick={onEnhance} fullWidth={isMobile}>
                Enhance
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Validate directors against registry">
            <Button size="small" variant="outlined" startIcon={<VerifiedUserIcon />} onClick={onValidate} fullWidth={isMobile}>
              Validate
            </Button>
          </Tooltip>
          {hasPermission('companies:create') && (
            <Tooltip title="Add director">
              <Button size="small" variant="outlined" startIcon={<PersonAddIcon />} onClick={onAddDirector} fullWidth={isMobile}>
                Director
              </Button>
            </Tooltip>
          )}
          {hasPermission('companies:create') && (
            <Tooltip title="Add shareholder">
              <Button size="small" variant="outlined" startIcon={<GroupAddIcon />} onClick={onAddShareholder} fullWidth={isMobile}>
                Shareholder
              </Button>
            </Tooltip>
          )}
          {hasPermission('companies:view') && (
            <Tooltip title="Download comprehensive PDF report">
              <Button
                size="small"
                variant="contained"
                color="secondary"
                startIcon={<DownloadIcon />}
                onClick={onDownloadPdf}
                fullWidth={isMobile}
              >
                PDF Report
              </Button>
            </Tooltip>
          )}
        </Stack>
      </Box>
    </Paper>
  );
};

export default CompanyHeader;
